import { AwsRum, AwsRumConfig } from 'aws-rum-web';

let rumClient: AwsRum | null = null;

/**
 * Initialize CloudWatch RUM client.
 * Call once at app startup (index.tsx). Safe to call multiple times — subsequent calls are no-ops.
 */
export function initRum(): AwsRum | null {
  if (rumClient) return rumClient;

  const appMonitorId = process.env.REACT_APP_RUM_APP_MONITOR_ID;
  const identityPoolId = process.env.REACT_APP_RUM_IDENTITY_POOL_ID;
  const region = process.env.REACT_APP_RUM_REGION || process.env.REACT_APP_COGNITO_REGION || 'us-east-1';

  if (!appMonitorId || !identityPoolId) {
    console.warn('[RUM] Missing configuration — skipping initialization. Set REACT_APP_RUM_APP_MONITOR_ID and REACT_APP_RUM_IDENTITY_POOL_ID.');
    return null;
  }

  try {
    const config: AwsRumConfig = {
      sessionSampleRate: 1,
      identityPoolId,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
    };

    rumClient = new AwsRum(appMonitorId, '1.0.0', region, config);
    console.log('[RUM] Initialized successfully');
    return rumClient;
  } catch (error) {
    console.warn('[RUM] Initialization failed:', error);
    return null;
  }
}

/**
 * Get the RUM client instance (or null if not initialized).
 */
export function getRum(): AwsRum | null {
  return rumClient;
}

// ─── Custom Event Helpers ────────────────────────────────────────────────────

/**
 * Record a layer data load event with performance metrics.
 */
export function recordLayerLoad(details: {
  layerId: string;
  layerName: string;
  pointCount: number;
  durationMs: number;
  cached: boolean;
  mode?: string;
  resolution?: number;
  h3Cells?: number;
  viewportZoom: number;
}) {
  rumClient?.recordEvent('layer_load', details);
}

/**
 * Record when deck.gl layers are rebuilt (render performance).
 */
export function recordDeckLayersBuild(details: {
  layerCount: number;
  totalPoints: number;
  h3DebugCells: number;
  durationMs: number;
  viewportZoom: number;
}) {
  rumClient?.recordEvent('deck_layers_built', details);
}

/**
 * Record viewport refresh cycle (pan/zoom triggers data reload).
 */
export function recordViewportRefresh(details: {
  visibleLayers: number;
  totalLayers: number;
  durationMs: number;
  successCount: number;
  errorCount: number;
  viewportZoom: number;
}) {
  rumClient?.recordEvent('viewport_refresh', details);
}

/**
 * Record when a user loads a dataset that exceeds a size threshold.
 */
export function recordDataOverload(details: {
  totalPoints: number;
  layerCount: number;
  viewportZoom: number;
  largestLayerName: string;
  largestLayerPoints: number;
}) {
  rumClient?.recordEvent('data_overload_warning', details);
}

/**
 * Record map setting changes (heatmap type, thresholds, etc.).
 */
export function recordSettingChange(details: {
  layerId: string;
  setting: string;
  oldValue: unknown;
  newValue: unknown;
}) {
  rumClient?.recordEvent('map_setting_change', details);
}

/**
 * Record a generic performance mark with duration.
 */
export function recordPerformanceMark(name: string, durationMs: number, metadata?: Record<string, unknown>) {
  rumClient?.recordEvent('performance_mark', { name, durationMs, ...metadata });
}
